import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
    AlertTriangle, ArrowLeft, CheckCircle2, Clipboard, Copy, Eye,
    EyeOff, KeyRound, Loader2, LockKeyhole, MoreHorizontal,
    RefreshCw, ShieldCheck, UserRound, UserRoundCog, Check, ChevronDown
} from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styles from './UserCredentials.module.css';
import { useStore } from '../../../context/StoreContext';

const API_ORIGIN = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001').replace(/\/api\/v1(?:\/public)?\/?$/, '').replace(/\/+$/, '');
const STAFF_API = `${API_ORIGIN}/api/v1/smiify/sm/staff`;

const ROLE_OPTIONS = [
    { value: 'STAFF', label: 'Staff', description: 'Day-to-day assigned operations' },
    { value: 'RECEPTIONIST', label: 'Receptionist', description: 'Front desk and billing workflow' },
    { value: 'MANAGER', label: 'Manager', description: 'Branch operations and team control' },
    { value: 'ADMIN', label: 'Admin', description: 'Broad administrative access' },
];

const getId = (record) => String(record?.id || record?._id || '');

const getName = (record) => {
    if (record?.name) return record.name;
    return `${record?.firstName || ''} ${record?.lastName || ''}`.trim() || 'Unnamed staff';
};

const makeUsername = (record) => {
    const name = getName(record).toLowerCase().replace(/[^a-z0-9]+/g, '.').replace(/^\.+|\.+$/g, '');
    const phoneSuffix = String(record?.phone || '').replace(/\D/g, '').slice(-4);
    return `${name || 'staff'}${phoneSuffix ? `.${phoneSuffix}` : ''}`;
};

const makePassword = () => {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    const values = Array.from({ length: 10 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]);
    return `Sm@${values.join('')}`;
};

const credentialStatus = (staff, createdCredentials, isDeactivated) => {
    if (isDeactivated) return 'Inactive';
    if (createdCredentials || staff?.hasAccess || staff?.loginAccountId || staff?.username) return 'Active';
    return 'Not created';
};

const UserCredentials = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { activeStore } = useStore();

    const routedStaff = location.state?.staff || null;
    const [staff, setStaff] = useState(routedStaff);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [actionLoading, setActionLoading] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [createdCredentials, setCreatedCredentials] = useState(null);
    const [isDeactivated, setIsDeactivated] = useState(routedStaff?.status === 'Suspended');
    const [formData, setFormData] = useState({ username: '', password: '', role: 'STAFF' });
    
    const [isRoleMenuOpen, setIsRoleMenuOpen] = useState(false);
    const roleDropdownRef = useRef(null);

    const storeId = activeStore?.id || activeStore?._id;
    const hasCredentials = Boolean(createdCredentials || staff?.hasAccess || staff?.loginAccountId || staff?.username);
    const status = credentialStatus(staff, createdCredentials, isDeactivated);

    const authHeaders = useMemo(() => {
        const token = localStorage.getItem('smiify_token');
        return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
                setIsRoleMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const loadStaff = async () => {
        if (!storeId) {
            setIsLoading(false);
            if (!routedStaff) setError('Select a store before managing staff credentials.');
            return;
        }
        setIsLoading(true); setError('');
        try {
            const response = await fetch(`${STAFF_API}?storeId=${encodeURIComponent(storeId)}`, { headers: authHeaders });
            const result = await response.json();
            if (!response.ok || !result.success) throw new Error(result.message || 'Unable to load staff account.');

            const match = (result.data || []).find((item) => getId(item) === String(userId));
            if (match) {
                setStaff(match);
                setIsDeactivated(match.status === 'Suspended' || match.status === 'Terminated');
            } else if (!routedStaff) throw new Error('Staff member was not found in the selected branch.');
        } catch (loadError) {
            setError(loadError.message || 'Unable to load staff account.');
        } finally { setIsLoading(false); }
    };

    useEffect(() => { loadStaff(); }, [storeId, userId]);

    useEffect(() => {
        if (!staff) return;
        setFormData((current) => ({
            username: current.username || staff.username || makeUsername(staff),
            password: current.password || makePassword(),
            role: current.role !== 'STAFF' || staff.role === 'STAFF' || staff.role === 'NONE' ? current.role : staff.role,
        }));
    }, [staff]);

    const selectedRole = ROLE_OPTIONS.find((role) => role.value === formData.role) || ROLE_OPTIONS[0];

    const updateField = (field, value) => {
        setFormData((current) => ({ ...current, [field]: value }));
        setError(''); setNotice('');
    };

    const regeneratePassword = () => { updateField('password', makePassword()); setShowPassword(true); };

    const handleCreateCredentials = async (event) => {
        event.preventDefault();
        const username = formData.username.trim().toLowerCase();
        const password = formData.password;

        if (username.length < 3) return setError('Username must contain at least 3 characters.');
        if (password.length < 8) return setError('Password must contain at least 8 characters.');

        setIsSaving(true); setError(''); setNotice('');

        try {
            const response = await fetch(`${STAFF_API}/${encodeURIComponent(userId)}/credentials`, {
                method: 'POST', headers: authHeaders, body: JSON.stringify({ username, password, role: formData.role }),
            });
            const result = await response.json();
            if (!response.ok || !result.success) throw new Error(result.message || 'Unable to create credentials.');

            setStaff(result.data || { ...staff, hasAccess: true, username, role: formData.role });
            setIsDeactivated(false);
            setCreatedCredentials({ username, password, role: formData.role });
            setFormData((current) => ({ ...current, username, password }));
            setNotice(result.alreadyExists ? 'Credentials were already linked.' : 'Credentials created successfully.');
        } catch (saveError) { setError(saveError.message || 'Unable to create credentials.'); } 
        finally { setIsSaving(false); }
    };

    // 🚀 NEW LOGIC: Secure Password Reset Feature
    const handleResetPassword = async () => {
        if (!window.confirm("Generate a new password for this user? The old password will stop working immediately.")) return;
        
        const newPassword = makePassword();
        setIsSaving(true); setError(''); setNotice('');
        
        try {
            const response = await fetch(`${STAFF_API}/${encodeURIComponent(userId)}`, {
                method: 'PUT', headers: authHeaders, body: JSON.stringify({ password: newPassword }),
            });
            const result = await response.json();
            if (!response.ok || !result.success) throw new Error(result.message || 'Failed to reset password.');

            const currentUsername = staff.username || formData.username;
            setCreatedCredentials({ username: currentUsername, password: newPassword, role: staff.role || formData.role });
            setShowPassword(true);
            setNotice('Password reset successfully. You can view and copy it now.');
        } catch (saveError) { setError(saveError.message || 'Unable to reset password.'); } 
        finally { setIsSaving(false); }
    };

    // 🚀 PERFECT COPY: Copies Username and Password together!
    const copyCredentials = async () => {
        const username = createdCredentials?.username || staff?.username || formData.username;
        const password = createdCredentials?.password;
        
        if (!password) {
            setError('Please reset the password first to copy both username and password.');
            return;
        }

        const role = createdCredentials?.role || staff?.role || formData.role;
        const text = `Username: ${username}\nPassword: ${password}\nRole: ${role}`;

        try {
            await navigator.clipboard.writeText(text);
            setNotice('Credentials copied to clipboard successfully!');
        } catch (copyError) { setError('Clipboard permission denied. Copy manually.'); }
    };

    const copyUsername = async () => {
        try {
            await navigator.clipboard.writeText(staff?.username || formData.username);
            setNotice('Username copied.');
        } catch (copyError) { setError('Unable to copy username.'); }
    };

    const handleManageAccess = () => navigate(`/users/${encodeURIComponent(userId)}/manage-access`, { state: { staff } });

    const handleDeactivate = async () => {
        if (!window.confirm(`Deactivate dashboard access for ${getName(staff)}?`)) return;
        setActionLoading('deactivate'); setError(''); setNotice('');
        try {
            const response = await fetch(`${STAFF_API}/${encodeURIComponent(userId)}/deactivate`, { method: 'PATCH', headers: authHeaders });
            const result = await response.json();
            if (!response.ok || !result.success) throw new Error(result.message || 'Unable to deactivate access.');

            setStaff((current) => ({ ...(result.data || current), status: 'Suspended', isActive: false }));
            setIsDeactivated(true); setNotice('Staff access deactivated.');
        } catch (deactivateError) { setError(deactivateError.message || 'Unable to deactivate access.'); } 
        finally { setActionLoading(''); }
    };

    if (isLoading) return <div className={styles.loadingPage}><Loader2 size={24} className={styles.spinner} /><span>Loading account…</span></div>;
    if (!staff) return <div className={styles.loadingPage}><AlertTriangle size={24} className={styles.errorIcon} /><span>{error || 'Staff not found.'}</span><button type="button" className={styles.outlineBtn} onClick={() => navigate(-1)}>Go back</button></div>;

    const accountName = getName(staff);
    const shortId = getId(staff).slice(-6).toUpperCase() || '------';

    return (
        <div className={styles.pageContainer}>
            <header className={styles.header}>
                <div className={styles.headerLeft}>
                    <button type="button" className={styles.backButton} onClick={() => navigate(-1)}><ArrowLeft size={16} /></button>
                    <div className={styles.headerCopy}>
                        <span className={styles.eyebrow}>USER ACCESS & CREDENTIALS</span>
                        <h1>Manage Identity</h1>
                    </div>
                </div>
                <div className={styles.headerActions}>
                    <span className={`${styles.statusBadge} ${status === 'Active' ? styles.statusActive : styles.statusMuted}`}>
                        {status === 'Active' ? <CheckCircle2 size={12} /> : <LockKeyhole size={12} />}{status}
                    </span>
                    <button type="button" className={styles.outlineBtn} onClick={() => navigate(-1)}>Done</button>
                </div>
            </header>

            <main className={styles.contentWrapper}>
                <div className={styles.contentInner}>
                    
                    <section className={styles.identityCard}>
                        <div className={styles.identityAvatar}>{accountName.charAt(0).toUpperCase()}</div>
                        <div className={styles.identityMain}>
                            <div className={styles.identityTitleRow}>
                                <h2>{accountName}</h2>
                                <span className={styles.staffId}>USR-{shortId}</span>
                            </div>
                            <p>{staff.designation || 'Staff member'} • {staff.phone || 'No phone number'}</p>
                        </div>
                        <div className={styles.identityMeta}>
                            <span>Primary Branch</span>
                            <strong>{activeStore?.storeName || activeStore?.name || 'Selected store'}</strong>
                        </div>
                    </section>

                    {error && <div className={styles.feedbackError}><AlertTriangle size={14} /><span>{error}</span></div>}
                    {notice && <div className={styles.feedbackNotice}><CheckCircle2 size={14} /><span>{notice}</span></div>}

                    {!hasCredentials ? (
                        <div className={styles.workspaceGrid}>
                            <section className={styles.card}>
                                <div className={styles.cardHeader}>
                                    <div className={styles.cardHeading}>
                                        <div className={styles.cardIcon}><KeyRound size={16} /></div>
                                        <div>
                                            <h2>Create credentials</h2>
                                            <p>Set the login details that this staff member will use.</p>
                                        </div>
                                    </div>
                                </div>
                                <form className={styles.form} onSubmit={handleCreateCredentials}>
                                    
                                    <div className={styles.fieldRow}>
                                        <div className={styles.field}>
                                            <label>Username</label>
                                            <div className={styles.inputWithAction}>
                                                <span className={styles.inputIcon}><UserRound size={14} /></span>
                                                <input value={formData.username} onChange={(e) => updateField('username', e.target.value)} placeholder="staff.username" spellCheck="false" />
                                                <button type="button" onClick={() => updateField('username', makeUsername(staff))} title="Generate unique username"><RefreshCw size={13} /></button>
                                            </div>
                                        </div>
                                        <div className={styles.field}>
                                            <label>Password</label>
                                            <div className={styles.inputWithAction}>
                                                <span className={styles.inputIcon}><LockKeyhole size={14} /></span>
                                                <input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => updateField('password', e.target.value)} placeholder="Min 8 chars" />
                                                <button type="button" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                                                <button type="button" onClick={regeneratePassword} title="Generate strong password"><RefreshCw size={13} /></button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.field} ref={roleDropdownRef}>
                                        <label>System Role & Permissions</label>
                                        <div className={styles.customSelectWrapper}>
                                            <button 
                                                type="button" 
                                                className={`${styles.selectTrigger} ${isRoleMenuOpen ? styles.selectTriggerActive : ''}`}
                                                onClick={() => setIsRoleMenuOpen(!isRoleMenuOpen)}
                                            >
                                                <div className={styles.selectTriggerLeft}>
                                                    <ShieldCheck size={18} className={styles.selectTriggerIcon} />
                                                    <div className={styles.selectTriggerText}>
                                                        <strong>{selectedRole.label}</strong>
                                                        <span>{selectedRole.description}</span>
                                                    </div>
                                                </div>
                                                <ChevronDown size={18} className={`${styles.chevronIcon} ${isRoleMenuOpen ? styles.chevronOpen : ''}`} />
                                            </button>

                                            {isRoleMenuOpen && (
                                                <div className={styles.selectDropdown}>
                                                    {ROLE_OPTIONS.map((role) => (
                                                        <div 
                                                            key={role.value} 
                                                            className={`${styles.selectOption} ${formData.role === role.value ? styles.selectOptionActive : ''}`}
                                                            onClick={() => {
                                                                updateField('role', role.value);
                                                                setIsRoleMenuOpen(false);
                                                            }}
                                                        >
                                                            <div className={styles.optionContent}>
                                                                <strong>{role.label}</strong>
                                                                <span>{role.description}</span>
                                                            </div>
                                                            {formData.role === role.value && <Check size={16} className={styles.checkIcon} />}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className={styles.formFooter}>
                                        <div className={styles.secureNote}><ShieldCheck size={14} /><span>Role sets default PBAC policies.</span></div>
                                        <button type="submit" className={styles.primaryBtn} disabled={isSaving}>
                                            {isSaving ? <Loader2 size={14} className={styles.spinnerWhite} /> : <KeyRound size={14} />}
                                            {isSaving ? 'Creating…' : 'Generate Identity'}
                                        </button>
                                    </div>
                                </form>
                            </section>
                            
                            <aside className={styles.sideColumn}>
                                <section className={styles.sideCard}>
                                    <div className={styles.previewHeader}><UserRoundCog size={14} /> Account Preview</div>
                                    <div className={styles.previewRows}>
                                        <div className={styles.previewRow}><span>Username</span><strong>{formData.username || '—'}</strong></div>
                                        <div className={styles.previewRow}><span>Role</span><strong>{selectedRole.label}</strong></div>
                                        <div className={styles.previewRow}><span>Branch</span><strong>{activeStore?.storeName || 'Selected store'}</strong></div>
                                    </div>
                                </section>
                                <section className={styles.infoBox}>
                                    <Clipboard size={16} />
                                    <div><strong>Keep password safe</strong><p>It is only visible now. Copy before leaving.</p></div>
                                </section>
                            </aside>
                        </div>
                    ) : (
                        <div className={styles.workspaceGrid}>
                            <section className={styles.card}>
                                <div className={styles.cardHeader}>
                                    <div className={styles.cardHeading}>
                                        <div className={`${styles.cardIcon} ${styles.iconSuccess}`}><ShieldCheck size={16} /></div>
                                        <div>
                                            <h2>Identity is Active</h2>
                                            <p>This user has verified portal access.</p>
                                        </div>
                                    </div>
                                    <span className={styles.rolePill}>{staff.role || createdCredentials?.role || 'STAFF'}</span>
                                </div>
                                <div className={styles.credentialRows}>
                                    <div className={styles.credentialRow}>
                                        <div className={styles.credentialLabel}><UserRound size={14} /><span>Username</span></div>
                                        <strong>{createdCredentials?.username || staff.username || formData.username || 'Not available'}</strong>
                                        <button type="button" className={styles.iconBtn} onClick={copyUsername} title="Copy Username"><Copy size={13} /></button>
                                    </div>
                                    
                                    {/* 🚀 FIXED: Password row with Reset logic */}
                                    <div className={styles.credentialRow}>
                                        <div className={styles.credentialLabel}><LockKeyhole size={14} /><span>Password</span></div>
                                        <strong className={styles.secretValue}>{createdCredentials?.password ? (showPassword ? createdCredentials.password : '••••••••••••') : '•••••••••••• (Encrypted)'}</strong>
                                        
                                        {createdCredentials?.password ? (
                                            <button type="button" className={styles.iconBtn} onClick={() => setShowPassword(!showPassword)}>
                                                {showPassword ? <EyeOff size={13} /> : <Eye size={13} />}
                                            </button>
                                        ) : (
                                            <button type="button" className={styles.actionCardBtn} style={{padding: '6px 12px', margin: 0}} onClick={handleResetPassword} disabled={isSaving}>
                                                {isSaving ? <Loader2 size={13} className={styles.spinner} /> : <RefreshCw size={13} />} 
                                                <span style={{fontSize: '11px', fontWeight: 800, color: 'var(--ink)'}}>Reset</span>
                                            </button>
                                        )}
                                    </div>

                                </div>
                                <div className={styles.actionGrid}>
                                    <button type="button" className={styles.actionCardBtn} onClick={handleManageAccess}>
                                        <div className={styles.actionCardIcon}><ShieldCheck size={16} /></div>
                                        <div className={styles.actionCardText}><strong>Matrix Policies</strong><span>Edit PBAC modules</span></div>
                                        <MoreHorizontal size={14} className={styles.chevron} />
                                    </button>
                                    <button type="button" className={styles.actionCardBtn} onClick={copyCredentials}>
                                        <div className={styles.actionCardIcon}><Copy size={16} /></div>
                                        <div className={styles.actionCardText}><strong>Copy Keys</strong><span>Copies Username & Password</span></div>
                                        <MoreHorizontal size={14} className={styles.chevron} />
                                    </button>
                                </div>
                            </section>
                            <aside className={styles.sideColumn}>
                                <section className={styles.successBox}>
                                    <CheckCircle2 size={16} />
                                    <div><strong>Setup Complete</strong><p>{createdCredentials ? 'Credentials issued in this session.' : 'Identity linked successfully.'}</p></div>
                                </section>
                                {isDeactivated ? (
                                    <section className={styles.lockedBox}>
                                        <LockKeyhole size={16} />
                                        <div><strong>Access Deactivated</strong><p>Profile is suspended and locked out.</p></div>
                                    </section>
                                ) : (
                                    <section className={styles.dangerBox}>
                                        <div className={styles.dangerHeader}>
                                            <AlertTriangle size={15} />
                                            <div><h3>Deactivate Access</h3><p>Lock this user out of the portal.</p></div>
                                        </div>
                                        <button type="button" className={styles.dangerBtn} onClick={handleDeactivate} disabled={actionLoading === 'deactivate'}>
                                            {actionLoading === 'deactivate' ? <Loader2 size={14} className={styles.spinner} /> : <LockKeyhole size={14} />}
                                            {actionLoading === 'deactivate' ? 'Deactivating…' : 'Revoke Access'}
                                        </button>
                                    </section>
                                )}
                            </aside>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default UserCredentials;